// CONSTANTS ---------------------------------------------------------------- //

/** Cap on raw move rows fetched for chart calculations. If the range holds
 *  more moves than this, charts are based on the first N and a warning shows. */
export const REPORTING_MOVE_LIMIT = 5000;

// TYPES ---------------------------------------------------------------- //

export type ReportingTab = 'operations' | 'appointments' | 'executive';

export type ReportingRange = 'last7' | 'last30' | 'last90' | 'rolling30' | 'thisMonth' | 'lastMonth' | 'custom';

/** Lightweight move shape used by reporting metric utilities. Mirrors the
 *  fields fetched by getReportingDashboard so pure utils can be unit tested
 *  without depending on the full generated GraphQL types. */
export type ReportingMove = {
  id: number;
  active?: number | null;
  cancel_status?: string | null;
  canceled_at?: string | null;
  customer_id?: number | null;
  payer_id?: number | null;
  createdat?: string | null;
  createdBy?: string | null;
  deliver_by?: string | null;
  /** Timestamp when delivery was marked successful (timestamptz, not a boolean) */
  delivery_successful?: string | null;
  delivery_time?: string | null;
  driver_id?: number | null;
  driver_name?: string | null;
  move_type?: string | null;
  pickup_time?: string | null;
  ready_by?: string | null;
  reference_num?: string | null;
  status?: string | null;
  consumer_name?: string | null;
  consumer_phone?: string | null;
  lane?: {
    id?: number | null;
    duration_sec?: number | null;
    distance_miles?: number | null;
  } | null;
  childRideMoves?: Array<{
    id?: number | null;
    status?: string | null;
    cancel_status?: string | null;
  }> | null;
};

/** Move audit record fetched separately for Executive KPI validation metrics */
export type ReportingMoveAudit = {
  id?: number | null;
  move_id?: number | null;
  pickup_location_within_bounds?: boolean | null;
  delivery_location_within_bounds?: boolean | null;
  move_duration_within_bounds?: boolean | null;
};

/** Business hours record used for appointment capacity estimation */
export type ReportingBusinessHours = {
  id?: number | null;
  day_of_week?: number | null;
  is_open?: boolean | null;
  open_time?: string | null;
  closed_time?: string | null;
};

/** Per-module visibility flags. Undefined means visible (opt-out default). */
export type ReportingModuleFlags = Record<string, boolean | undefined>;

/** Visibility config for a single reporting tab. */
export type ReportingTabConfig = {
  enabled?: boolean;
  metrics?: ReportingModuleFlags;
  charts?: ReportingModuleFlags;
  kpis?: ReportingModuleFlags;
};

/**
 * Reporting dashboard visibility config. Mirrors the `reporting` definition in
 * @hopdrive/sdk-configs. Read from customerConfig.reporting as plain JSON.
 * Replaced wholesale per config level (shallow merge), so absent leaves are
 * treated as visible.
 */
export type ReportingConfig = {
  enabled?: boolean;
  tabs?: {
    operations?: ReportingTabConfig;
    appointments?: ReportingTabConfig;
    executive?: ReportingTabConfig;
  };
};

/** Module group keys within a tab. */
export type ReportingModuleGroup = 'metrics' | 'charts' | 'kpis';

/** Appointment management config parsed from rooftop config */
export type AppointmentManagementConfig = {
  enabled?: boolean;
  driver_count?: number;
  move_buffer_minutes?: number;
  strategy?: string;
  offset_max_minutes?: number;
  offset_increment_minutes?: number;
  enforcement_mode?: string;
};
